"use client";

import React, { useState } from "react";
import { useRouter } from "next/navigation";
import { Button } from "@/components/ui/button";
import { X, LogIn, User, LogOut } from "lucide-react";
import Link from "next/link";
import { getDisplayNameWithPartialEmail } from "@/lib/format-email";
import { disconnectSession } from "@/actions/session";

interface AuthBannerProps {
  onDismiss?: () => void;
  user?: {
    id: string;
    email: string;
    name: string | null;
    role: string;
  } | null;
}

export function AuthBanner({ onDismiss, user }: AuthBannerProps) {
  const router = useRouter();
  const [isDismissed, setIsDismissed] = useState(false);
  const [isDisconnecting, setIsDisconnecting] = useState(false);

  const handleDismiss = () => {
    setIsDismissed(true);
    onDismiss?.();
  };

  const handleDisconnect = async () => {
    setIsDisconnecting(true);
    try {
      await disconnectSession();
      router.refresh();
    } finally {
      setIsDisconnecting(false);
    }
  };

  const handleLogin = async () => {
    // Set returnTo cookie so main platform knows to redirect to webinar
    document.cookie = "returnTo=webinar; path=/; max-age=3600; SameSite=Lax";
    
    const mainPlatformUrl =
      process.env.NEXT_PUBLIC_MAIN_PLATFORM_URL || "https://edukateafrica.com";
    router.push(`${mainPlatformUrl}/login`);
  };

  if (user) {
    const displayName = getDisplayNameWithPartialEmail(user.name, user.email);
    
    return (
      <div className="w-full bg-green-50 border-b border-green-200 px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <User className="h-5 w-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm text-green-900 font-medium">
                ✓ Account Connected: {displayName}
              </p>
              <p className="text-xs text-green-700 mt-1">
                Your registration will be automatically linked to your Edukate Africa account.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <Button
              onClick={handleDisconnect}
              disabled={isDisconnecting}
              variant="ghost"
              size="sm"
              className="text-green-600 hover:text-green-700 hover:bg-green-100 flex items-center gap-2"
            >
              <LogOut className="h-4 w-4" />
              {isDisconnecting ? "Disconnecting..." : "Disconnect"}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              onClick={handleDismiss}
              className="text-green-600 hover:text-green-700 hover:bg-green-100"
            >
              <X className="h-4 w-4" />
              <span className="sr-only">Dismiss</span>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-blue-50 border-b border-blue-200 px-4 py-4">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1">
          <LogIn className="h-5 w-5 text-blue-600 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm text-blue-900 font-medium">
              Have an Edukate Africa account?
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Log in to auto-verify your registration and enjoy instant access to the summit.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <Button
            onClick={handleLogin}
            variant="default"
            size="sm"
            className="bg-blue-600 hover:bg-blue-700"
          >
            Log In
          </Button>

          <Button
            variant="ghost"
            size="sm"
            onClick={handleDismiss}
            className="text-blue-600 hover:text-blue-700 hover:bg-blue-100"
          >
            <X className="h-4 w-4" />
            <span className="sr-only">Dismiss</span>
          </Button>
        </div>
      </div>
    </div>
  );
}
